import gsap from "gsap";
import className from "classnames/bind";
import { forwardRef, useCallback, useEffect, useMemo } from "react";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { AnimatePresence, motion } from "motion/react";
import { easeOutQuad } from "@/core/easing";
import { useHeader } from "@/providers/HeaderContext";
import { useSearch } from "@/providers/SearchContext";
import { Container, Button, Filters, QueryPagination } from "@components";
import { MergedResult } from "@/utils/interleaveResults";
import Result from "./Result";
import ShopResult from "./ShopResult";
import NoResults from "../NoResults";
import { GetPageCategoriesQuery, GetSearchPageQuery, SearchQuery } from "@graphqlTypes";
import ExternalArrow from "@/assets/svg/external-arrow.svg";

import styles from "./RockHallResults.module.scss";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
const cx = className.bind(styles);

export type Result = SearchQuery["search"]["nodes"][number];

type RockHallResultsProps = {
  pageResults: MergedResult[];
  noResults: GetSearchPageQuery["rhPage"]["searchPageData"]["noResults"];
  categories: GetPageCategoriesQuery["pageCategories"]["nodes"];
  pressReleaseSearchImage: AcfMediaFragment;
  searchTerm: string;
  headline: string;
  totalCount: number;
  showPaging: boolean;
  typeFilters: string[];
  onTypeFilterClick: (type: string) => void;
  brandFilterLabel: string;
  termCounts: { termSlug?: string; count?: number }[];
};

const itemVariants = (index: number) => ({
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: 0.35 + index * 0.1,
      ease: easeOutQuad,
    },
  },
});

const RockHallResults = forwardRef<HTMLDivElement, RockHallResultsProps>(
  (
    {
      pageResults,
      totalCount,
      showPaging,
      noResults,
      searchTerm,
      headline,
      categories,
      brandFilterLabel,
      termCounts,
      onTypeFilterClick,
      typeFilters,
      pressReleaseSearchImage,
    },
    ref
  ) => {
    const {
      currentPage,
      perPage,
      setCurrentPage,
    } = useSearch();
    const labelOverrides: { [key: string]: string } = {
      brand: brandFilterLabel,
    };
    const { setIgnoreScroll, headerRef, headerVisible } = useHeader();
    const shopCount = useMemo(() => termCounts.find((t) => t.termSlug === "shop")?.count ?? 0, [termCounts]);
    const shopSearchUrl = `https://shop.rockhall.com/search?q=${encodeURIComponent(searchTerm)}`;
    const { labels: { postTypeLabels: { shop } = { } } = {} } = usePageWrapperData();

    const modifiedCategories = [
      ...[...categories].map((category) => ({
        ...category,
        name: labelOverrides[category.slug] || category.name,
      })),
      { slug: "shop", name: shop || 'Shop' },
    ];

    const categoriesSorted = [...modifiedCategories].sort((a, b) => {
      if (a.slug === "brand") return 1;
      if (b.slug === "brand") return -1;
      if (a.slug === "shop") return 1;
      if (b.slug === "shop") return -1;
      return a.name.localeCompare(b.name);
    });

    const onPageChange = useCallback(
      (page: number) => {
        const results = document.getElementById("rockhall-results");
        setIgnoreScroll(true);
        gsap.to(window, {
          scrollTo: {
            y: results,
            offsetY: headerVisible ? headerRef?.current?.offsetHeight ?? 0 : 0,
          },
          duration: 0.5,
          ease: "power2.out",
          onComplete: () => setIgnoreScroll(false),
        });
        setCurrentPage(page);
      },
      [headerVisible]
    );

    useEffect(() => {
      gsap.registerPlugin(ScrollToPlugin);
    }, []);
    return (
      <div className={cx(["rockhall-results"])} ref={ref} id="rockhall-results">
        <Container className={cx("container")}>
          <div className={cx("rockhall-results__header")}>
            <h2 className={cx("heading", "title-uc-l")}>{headline}</h2>
          </div>
          <div className={cx("rockhall-results__filters")}>
            <Filters
              filterButtons={categoriesSorted}
              typeFilters={typeFilters}
              termCounts={termCounts}
              handleFilterClick={onTypeFilterClick}
            />
            {shopCount > 0 && (
              <Button
                className={cx("shop-cta")}
                label="See all shop results"
                url={shopSearchUrl}
                target="_blank"
                icon={ExternalArrow}
              />
            )}
          </div>
          <AnimatePresence mode="wait">
            {pageResults.length > 0 ? (
              <>
                <motion.div
                  className={cx("rockhall-results__cards")}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  key={pageResults.map((r, i) => r.type === 'wp' ? r.data.uri : `shop-${i}`).join("-")}
                  transition={{
                    delayChildren: 0.3,
                    duration: 0.5,
                    ease: easeOutQuad,
                  }}
                >
                  {pageResults.map((item, index) =>
                    item.type === 'wp' ? (
                      <Result key={item.data.uri || index} {...item.data} pressReleaseSearchImage={pressReleaseSearchImage} />
                    ) : (
                      <ShopResult key={`shop-${item.data.onlineStoreUrl || index}`} {...item.data} />
                    )
                  )}
                </motion.div>
                {showPaging && (
                  <QueryPagination
                    className={cx("rockhall-results__pagination")}
                    size="small"
                    currentPage={currentPage}
                    totalCount={totalCount}
                    perPage={perPage}
                    onPageChange={onPageChange}
                  />
                )}
              </>
            ) : (
              <NoResults
                key="no-results"
                variants={itemVariants(0)}
                initial="initial"
                animate="animate"
                exit="exit"
                {...noResults}
                searchTerm={searchTerm}
              />
            )}
          </AnimatePresence>
        </Container>
      </div>
    );
  }
);

export default motion.create(RockHallResults);
